import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";
import { InvocationStatusEnum } from "../../generated/core-api";

export const TERMINAL_INVOCATION_STATUSES: string[] = [
  InvocationStatusEnum.COMPLETED,
  InvocationStatusEnum.FAILED,
  InvocationStatusEnum.TERMINATED,
];

export function isTerminalInvocationStatus(
  status: string | null | undefined
): boolean {
  return !!status && TERMINAL_INVOCATION_STATUSES.includes(status);
}

/**
 * Poll `GET /api/v1/agents/{id}/invocations/{workflowId}` every 2s until the
 * invocation reaches a terminal status — same poll-until-terminal shape as
 * `src/shadow-runs/hooks/useShadowRunProgress.ts`. Shared by the invoke
 * hooks (`useValidateCompanyShellProvision`/`useCommitCompanyShellProvision`,
 * which start a workflow then poll it) and the Results screen (which only
 * resumes polling an already-started commit after navigating from Review).
 */
export function useInvocationStatusPoll(
  agentId: string | null | undefined,
  workflowId: string | null | undefined
) {
  return useQuery({
    queryKey: ["companyShellProvisionInvocation", agentId, workflowId],
    queryFn: () => api.getInvocationStatus(agentId as string, workflowId as string),
    enabled: !!agentId && !!workflowId,
    refetchInterval: (query) =>
      isTerminalInvocationStatus(query.state.data?.status) ? false : 2000,
    // Once terminal, the result never changes — don't let a window-focus
    // regain re-fetch a settled invocation (and needlessly re-fire the
    // wizard-state sync effect downstream). The `refetchInterval` above
    // already drives the only refresh we need while it's still running.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}
